import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContextHardened } from '../Helper/ContextHelper';
import { scrollTop } from '../Helper/ScrollTop';
import { PageAccessMap, SUBMENU_ORDER, SubMenu } from 'src/Menus';
import useEkko from './EkkoProvider';
import useStableCallback from '../Hooks/useStableCallback';

interface Context {
  sortedMenuList: SubMenuState[];
  scrollRef: React.RefObject<HTMLDivElement>;
  scrollTop: () => void;
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  moveEntry: (subMenu: SubMenu, from: number, to: number) => void;
  toggleSubMenu: (subMenu: SubMenu) => void;
  /**
   * The section that owns the current route, or undefined on routes not
   * in the menu (e.g. /me/*, /login). Always rendered expanded and pinned
   * (it can't be collapsed while you're on one of its pages).
   */
  activeSubMenu: SubMenu | undefined;
  /**
   * Whether a section is currently expanded. A section is open iff it is
   * the active (route-owned) section OR the single user-opened "extra"
   * section — at most two open at once, one on /me/* style routes.
   */
  isExpanded: (subMenu: SubMenu) => boolean;
  /**
   * Set the active (route-owned) section. Wired to navigation; pass undefined
   * for routes outside the menu. On a real section change the previous active
   * section is demoted into the single "extra" slot, so the section you came
   * from stays open (current + previous, capped at two).
   */
  setActiveSubMenu: (subMenu: SubMenu | undefined) => void;
}
const [NavigationContext, useNavigation] = createContextHardened<Context>('Navigation');
export default useNavigation;

export interface SubMenuState {
  subMenu: SubMenu;
  accessKeys: string[];
}

const STORAGE_KEY_MENU: string = 'ESLMANAGER_NAVIGATION_MENU';

// Persists the drawer's collapsed/expanded (icon-only vs. labeled) state so
// it survives reloads and logins instead of always resetting to collapsed.
const STORAGE_KEY_OPEN: string = 'ESLMANAGER_NAVIGATION_OPEN';

// Per-section expanded state is no longer persisted — which sections are
// open is derived from the current route plus one transient user-opened
// extra (see toggleSubMenu / isExpanded). localStorage holds only the
// drag-sort order of entries within each section.
const defaultStatesBySubMenu: Record<SubMenu, SubMenuState> = Object.entries(PageAccessMap).reduce(
  (carry, [key, access]) => {
    // URL-only pages (no `menu`) never appear in the sidebar.
    if (access.menu === undefined) {
      return carry;
    }
    const { subMenu } = access.menu;

    if (Object.keys(carry).includes(subMenu)) {
      carry[subMenu].accessKeys.push(key);
    } else {
      carry[subMenu] = { subMenu, accessKeys: [key] };
    }

    return carry;
  },
  {} as Record<SubMenu, SubMenuState>,
);

/**
 * Default sidebar order derived from SUBMENU_ORDER in Menus.tsx so that
 * "what shows up where" is decoupled from the entry-iteration order of
 * PageAccessMap. Any submenu missing from SUBMENU_ORDER (e.g. one a dev
 * forgot to add) falls back to the end so it's still visible.
 */
const defaultOrderedSubMenuStates = (): SubMenuState[] => {
  const inOrder = SUBMENU_ORDER.map((sm) => defaultStatesBySubMenu[sm]).filter(Boolean);
  const orderedSet = new Set(inOrder.map((s) => s.subMenu));
  const tail = Object.values(defaultStatesBySubMenu).filter((s) => !orderedSet.has(s.subMenu));
  return [...inOrder, ...tail];
};

interface Props {
  children: React.ReactNode;
}
export const NavigationProvider = ({ children }: Props): React.JSX.Element => {
  const { hasPermission } = useEkko();
  // Restore the last collapsed/expanded choice; default to collapsed
  // (icon-only) for first-time users.
  const [isOpen, setOpen] = useState<boolean>(() => localStorage.getItem(STORAGE_KEY_OPEN) === 'true');
  const [sortedMenuList, setSortedMenuList] = useState<SubMenuState[]>([]);

  // Section the current route lives in (undefined on /me/* etc.) — always
  // expanded and pinned. Plus one transient "extra": the section the user
  // opened by hand, OR the section they just navigated away from (demoted on
  // navigation). Either way only one extra exists, so at most active + extra
  // are open at once.
  const [activeSubMenu, setActiveSubMenuState] = useState<SubMenu | undefined>(undefined);
  const [extraSubMenu, setExtraSubMenu] = useState<SubMenu | undefined>(undefined);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollTopCallback = useCallback(() => {
    if (scrollRef.current === null) {
      return;
    }

    scrollTop(scrollRef.current);
  }, []);

  const moveEntry = useCallback(
    (subMenu: SubMenu, from: number, to: number) => {
      const subMenuIndex = sortedMenuList.findIndex((m) => m.subMenu === subMenu);
      if (subMenuIndex >= 0 && from >= 0 && to >= 0 && from !== to) {
        // working on a copy
        const newOrderedMenu = [...sortedMenuList];
        const subMenu = newOrderedMenu[subMenuIndex];

        if (subMenu.accessKeys.length - 1 > to) {
          const [moved] = subMenu.accessKeys.splice(from, 1);
          subMenu.accessKeys.splice(to, 0, moved);
          newOrderedMenu[subMenuIndex] = subMenu;

          setSortedMenuList(newOrderedMenu);
        }
      }
    },
    [sortedMenuList],
  );

  const toggleSubMenu = useCallback(
    (subMenu: SubMenu) => {
      // The active (current-route) section is pinned open — clicking it is
      // a no-op. Otherwise this is the single "extra" slot: clicking the
      // open extra closes it, clicking any other section opens it there
      // (evicting whatever extra was open before).
      if (subMenu === activeSubMenu) {
        return;
      }
      setExtraSubMenu((prev) => (prev === subMenu ? undefined : subMenu));
    },
    [activeSubMenu],
  );

  const setActiveSubMenu = useStableCallback((subMenu: SubMenu | undefined) => {
    // Use the functional updater so we read the *previous* active section
    // reliably (useStableCallback's ref only refreshes in an effect, so the
    // closure value can be stale).
    setActiveSubMenuState((prevActive) => {
      // When the active section actually changes, demote the previous one into
      // the single "extra" slot so the section you came from stays open
      // (current + previous, still capped at two). Same-section navigation
      // leaves the extra untouched. setExtraSubMenu here is idempotent, so it
      // is safe under React's double-invoked updaters.
      if (prevActive !== subMenu) {
        setExtraSubMenu(prevActive);
      }
      return subMenu;
    });
  }, []);

  const isExpanded = useCallback(
    (subMenu: SubMenu): boolean => subMenu === activeSubMenu || subMenu === extraSubMenu,
    [activeSubMenu, extraSubMenu],
  );

  useEffect(() => {
    const storedMenuList = localStorage.getItem(STORAGE_KEY_MENU);
    const decodedList = storedMenuList === null ? [] : JSON.parse(storedMenuList);

    if (!Array.isArray(decodedList) || decodedList.length === 0) {
      // if we can't restore anything, we fallback to the default.
      setSortedMenuList(defaultOrderedSubMenuStates());
      return;
    }

    // Section order is code-owned (SUBMENU_ORDER in Menus.tsx) and open
    // state is route-driven, so the persisted entries are consulted for
    // exactly one thing: the user's drag-sort order of entries within a
    // section. Walk them into a keyed lookup, dropping unknown submenus
    // and stale access keys, then rebuild in SUBMENU_ORDER. (Reading the
    // legacy `title`/`isOpen` shape stays backward-compatible — we just
    // ignore `isOpen` now.)
    const statesBySubMenu: Record<string, SubMenuState> = {};
    for (const entry of decodedList) {
      if (!entry || typeof entry !== 'object') {
        continue;
      }
      const sm: SubMenu | undefined = (entry.subMenu ?? entry.title) as SubMenu | undefined;
      if (sm === undefined) {
        continue;
      }
      const defaultState = defaultStatesBySubMenu[sm];
      if (defaultState === undefined) {
        // Unknown / renamed submenu — skip it; it rebuilds from defaults.
        continue;
      }

      const storedKeys: string[] = Array.isArray(entry.accessKeys) ? entry.accessKeys : defaultState.accessKeys;
      const filteredKeys = storedKeys.filter((k) => defaultState.accessKeys.includes(k));
      const missingKeys = defaultState.accessKeys.filter((k) => !filteredKeys.includes(k));

      statesBySubMenu[sm] = {
        subMenu: sm,
        accessKeys: [...filteredKeys, ...missingKeys],
      };
    }

    // Build the final array in SUBMENU_ORDER, falling back to defaults
    // for any submenu the user hasn't seen yet.
    setSortedMenuList(defaultOrderedSubMenuStates().map((d) => statesBySubMenu[d.subMenu] ?? d));
  }, []);

  /** store on sorting change */
  useEffect(() => {
    if (sortedMenuList.length === 0) {
      return;
    }
    localStorage.setItem(STORAGE_KEY_MENU, JSON.stringify(sortedMenuList));
  }, [sortedMenuList]);

  /** store the drawer collapsed/expanded choice */
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_OPEN, String(isOpen));
  }, [isOpen]);

  const storageEventHandler = useStableCallback((event: StorageEvent) => {
    switch (event.key) {
      case STORAGE_KEY_MENU:
        if (event.newValue !== null && !['', '[]'].includes(event.newValue)) {
          setSortedMenuList(JSON.parse(event.newValue));
        }
        break;
      case STORAGE_KEY_OPEN:
        if (event.newValue !== null) {
          setOpen(event.newValue === 'true');
        }
        break;
    }
  }, []);

  /** sync storage accross contexts */
  useEffect(() => {
    addEventListener('storage', storageEventHandler);
    return () => {
      removeEventListener('storage', storageEventHandler);
    };
  }, [storageEventHandler]);

  const permittedMenuList = useMemo(
    () =>
      sortedMenuList.map((sm) => {
        const { accessKeys } = sm;
        const permittedKeys = accessKeys.filter((k) => hasPermission(PageAccessMap[k].permission));
        return { ...sm, accessKeys: permittedKeys };
      }),
    [sortedMenuList, hasPermission],
  );

  return (
    <NavigationContext.Provider
      value={{
        scrollRef,
        sortedMenuList: permittedMenuList,
        scrollTop: scrollTopCallback,
        isOpen,
        setOpen,
        moveEntry,
        toggleSubMenu,
        activeSubMenu,
        isExpanded,
        setActiveSubMenu,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};
